import { FileResponse } from '../api/Api';
import { uuid } from './uuid';
import { newPipeline, BlockBlobClient, AnonymousCredential } from '@azure/storage-blob';
import { AbortController as AzureAbortController } from '@azure/abort-controller';
import { createPromiseViewModel, PromiseViewModel } from '../viewmodel/PromiseViewModel';
import { isGcsSignedUrl } from './isGcsSignedUrl';

export function aurorStorageFileUploader(
    file: File,
    createFileResponse: (fileId: string) => Promise<FileResponse>,
): PromiseViewModel<string> {
    return createPromiseViewModel(
        transformToMobxFlow(async () => {
            return await uploadAndGetFileId(file, createFileResponse);
        })(),
    );
}

export async function uploadAndGetFileId(
    file: File,
    createFileResponse: (fileId: string) => Promise<FileResponse>,
) {
    const fileId = uuid();
    const response = await createFileResponse(fileId);
    await uploadBrowserData(response.uploadUrl, file as Blob, fileId);
    return fileId;
}

const uploadBrowserData = (uploadUrl: string, blob: Blob, fileId: string) =>
    isGcsSignedUrl(uploadUrl) ? uploadToGcs(uploadUrl, blob) : uploadToAzure(uploadUrl, blob, fileId);

// Azure (default): the SAS URL is driven by the Azure Blob SDK — a block-blob upload that sets x-ms-*
// headers and stores the fileId as blob metadata. Unchanged from before the GCS work.
const uploadToAzure = (uploadUriWithSasToken: string, blob: Blob, fileId: string) => {
    const pipeline = newPipeline(new AnonymousCredential());

    const aborter = new AzureAbortController();

    const blobClient = new BlockBlobClient(uploadUriWithSasToken, pipeline);

    const promise = blobClient.uploadData(blob, {
        abortSignal: aborter.signal,
        blobHTTPHeaders: {
            blobContentType: blob.type,
        },
        metadata: fileId ? { fileId } : undefined,
        maxSingleShotSize: 100000000, // 100 MB is the max Content-Length for Azure CDN
    });

    Object.assign(promise, {
        cancel: () => {
            aborter.abort();
        },
    });
    return promise;
};

// GCS: GcsFileService signs a plain V4 PUT URL and deliberately leaves Content-Type out of the
// signature, so a single raw-bytes PUT is all that's needed — the Azure Blob SDK's protocol does not
// apply here. The fileId is not sent as object metadata: x-goog-meta-* headers would have to be part of
// the signature, and it is already encoded in the object path ({fileId}/{fileType}).
const uploadToGcs = (uploadUrl: string, blob: Blob) => {
    const aborter = new AbortController();

    const promise = (async () => {
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: blob.type ? { 'Content-Type': blob.type } : undefined,
            signal: aborter.signal,
        });
        if (!response.ok) {
            const body = await response.text().catch(() => '');                                                                    
            throw new Error(`GCS upload failed with ${response.status} ${response.statusText}${body ? `: ${body}` : ''}`); 
        }
    })();

    Object.assign(promise, {
        cancel: () => {
            aborter.abort();
        },
    });
    return promise;
};
